// ─── Generic default column-header renderer ─────────────────────────────────
//
// Every DataGrid consumer otherwise hand-writes an identical
// `renderHeader(columnKey, ctx)`: read the column's `ColumnMeta` flags,
// derive enum options/counts (or a numeric domain) from the current row set,
// and forward ~15 props to `<DataGridColumnHeader>`. It is fully derivable
// from `columnMeta` + a small context object, so this provides that default
// implementation once.
//
// Crucially, this is what makes `ColumnMeta.groupable` actually sufficient on
// its own: the group-by affordance (`isGrouped`/`onToggleGroup`) is wired
// here from `meta.groupable` + the context, so a consuming table only has to
// set `groupable: true` on a column — it no longer also has to remember to
// thread `groupByColumnKey`/`onToggleGroup` through its own header renderer
// (a step TransactionsTableV3 had silently omitted, leaving `groupable: true`
// there doing nothing).
//
// A table with a bespoke header for one column (e.g. a combined column) can
// still special-case that key and delegate every other column to this.

import type { ReactNode } from 'react';
import { DataGridColumnHeader } from './DataGridColumnHeader';
import { getEnumFilterCounts, getEnumFilterOptions, getNumericDomain } from './gridEngine';
import type { ColumnMetaMap, FilterValue } from './types';
import { DATE_LASTDAYS_FILTER_SUFFIX, DATE_RANGE_FILTER_SUFFIX } from './types';

/**
 * The minimal context a default-rendered header needs. Both PropertiesTable's
 * and TransactionsTableV3's own cell/header contexts are supersets of this,
 * so they satisfy it structurally.
 */
export interface DataGridHeaderContext<TRow, K extends string> {
  columnMeta: ColumnMetaMap<TRow, K>;
  /**
   * Rows used to derive filter-dropdown options/counts — should be the
   * GROUP-quick-filtered set (not the per-column-filtered set), so selecting
   * one option in a column's own filter doesn't make that column's other
   * options vanish. `useDataGrid` returns exactly this as `groupFilteredRows`.
   */
  rows: TRow[];
  sortColumns: string[];
  sortOrders: ('asc' | 'desc' | 'none')[];
  filters: Record<string, FilterValue>;
  onSort: (columnKey: string, order: 'asc' | 'desc' | 'none') => void;
  onFilterChange: (columnKey: string, value: FilterValue) => void;
  /**
   * The active group-by column, if any. Enables the group-by affordance when
   * combined with `onToggleGroup` — a groupable column shows a "group by this
   * column" icon, or a removable badge when it IS this key.
   */
  groupByColumnKey?: K | null;
  /** Toggles a column as the group-by column (starts grouping, or stops it if already grouping). */
  onToggleGroup?: (columnKey: K) => void;
}

/**
 * Default `renderHeader` implementation for a DataGrid column — reads the
 * column's `ColumnMeta` and the grid context and renders a fully-wired
 * `<DataGridColumnHeader>` (sort + enum/range/date filter + group-by toggle).
 * `ColumnMeta.groupable` alone is enough to get the group-by affordance; no
 * extra per-table wiring is required.
 *
 * @example
 * // In a table: pass straight through, or special-case a column then fall back.
 * renderHeader={(key) => renderDataGridHeader(key, ctx)}
 */
export function renderDataGridHeader<TRow, K extends string>(columnKey: K, ctx: DataGridHeaderContext<TRow, K>): ReactNode {
  const meta = ctx.columnMeta[columnKey];
  const filterType = meta.filterType ?? 'enum';
  const isEnum = !!meta.filterable && filterType === 'enum';
  const isRange = !!meta.filterable && filterType === 'range';

  const filterOptions = isEnum ? getEnumFilterOptions(ctx.rows, columnKey, ctx.columnMeta) : undefined;
  const filterCounts = isEnum ? getEnumFilterCounts(ctx.rows, columnKey, ctx.columnMeta) : undefined;
  const domain = isRange ? (getNumericDomain(ctx.rows, columnKey, ctx.columnMeta) ?? undefined) : undefined;

  const isGrouped = ctx.groupByColumnKey === columnKey;

  return (
    <DataGridColumnHeader
      columnKey={columnKey}
      label={meta.label}
      sortable={meta.sortable}
      filterable={meta.filterable}
      filterType={filterType}
      sortColumns={ctx.sortColumns}
      sortOrders={ctx.sortOrders}
      onSort={ctx.onSort}
      filterOptions={filterOptions}
      filterCounts={filterCounts}
      domain={domain}
      currentFilter={ctx.filters[columnKey]}
      dateRangeFilter={ctx.filters[`${columnKey}${DATE_RANGE_FILTER_SUFFIX}`]}
      lastDaysFilter={ctx.filters[`${columnKey}${DATE_LASTDAYS_FILTER_SUFFIX}`]}
      onFilterChange={ctx.onFilterChange}
      renderFilterOption={meta.renderFilterOption}
      groupable={meta.groupable}
      isGrouped={isGrouped}
      // `DataGridColumnHeader.onToggleGroup` is typed `(columnKey: string)`,
      // but it only ever calls it back with this column's own `columnKey`
      // (a `K`), so narrowing the arg is safe.
      onToggleGroup={ctx.onToggleGroup ? (key) => ctx.onToggleGroup!(key as K) : undefined}
    />
  );
}
